/* eslint-disable yoda */
/* eslint-disable no-console */
import React, { useCallback } from 'react';
import { useToast } from '@ps-design-system/components/Toast';
import { FormattedMessage } from 'react-intl';

import {
  createSpaceImage,
  deleteSpaceImage,
  updateSpaceImage,
  createSpaceEmbedMedia,
  updateSpaceEmbedMediaOrder,
  deleteSpaceEmbedMedia,
  getSpaceImages,
  getSpaceEmbedMedia
} from '../api/spaceApi';
import useUploaderStore from '../store/useUploaderStore';
import { openCloudinaryWidget } from '../utils/cloudinary.utils';
import { formatMediaForUploader, getOptimizedThumbnailUrl } from '../utils/format.utils';
import { enforceCoverPhotoRule, getNextSortOrder, isEmbedMedia } from '../utils/media.utils';
import { buildAllMedia } from '../utils/buildAllMedia.utils';
import { useUploaderPageData } from '../context/UploaderPageContext';
import { TOAST_DURATION, CLOUDINARY_IMAGE_TRANSFORMATION } from '../constants/actions.consts';
import { datadogRum } from '@datadog/browser-rum';

export const useUploaderSpaceActions = () => {
  const media = useUploaderStore((state) => state.media);
  const setMedia = useUploaderStore((state) => state.setMedia);
  const addImage = useUploaderStore((state) => state.addImage);
  const updateOrAddMedia = useUploaderStore((state) => state.updateOrAddMedia);
  const removeMedia = useUploaderStore((state) => state.removeMedia);

  const { spaceId, intl } = useUploaderPageData();
  const { toast } = useToast();

  const fetchMedia = useCallback(async () => {
    if (!spaceId) {
      return [];
    }

    try {
      let data;
      if (spaceId) {
        const [imagesData, embedsData] = await Promise.all([
          getSpaceImages(spaceId),
          getSpaceEmbedMedia(spaceId)
        ]);
        data = {
          ...imagesData,
          embed_media: embedsData
        };
      }
      const formattedData = buildAllMedia(data);
      return formattedData;
    } catch {
      return [];
    }
  }, [spaceId]);

  const handlePhotoUpload = useCallback(async () => {
    try {
      const cloudinaryResponse = await openCloudinaryWidget(intl);
      if (cloudinaryResponse) {
        const processUploads = cloudinaryResponse.map(async (fileInfo) => {
          const savedImageObject = await createSpaceImage({
            url: fileInfo.url.replace('upload/', `upload/${CLOUDINARY_IMAGE_TRANSFORMATION}`),
            parentId: spaceId
          });
          const formmatedData = formatMediaForUploader(savedImageObject?.data?.image);
          if (formmatedData.id) {
            addImage(formmatedData);
          }
        });

        await Promise.all(processUploads);
      }
    } catch (error) {
      datadogRum.addError(error, "Error uploading photo to cloudinary", {
        spaceId,
      });
    }
  }, [addImage, intl, spaceId]);

  const handleEmbedUpload = useCallback(async (url) => {
    const currentMedia = useUploaderStore.getState().media;
    let sortOrder = getNextSortOrder(currentMedia);
    const payload = [{ url, sort_order: sortOrder }];

    try {
      const newEmbedItems = await createSpaceEmbedMedia(spaceId, payload);
      let formattedNewMedia = [];

      if (newEmbedItems && newEmbedItems.length > 0) {
        formattedNewMedia = newEmbedItems.map(item => {
          let thumbnailUrl = getOptimizedThumbnailUrl(item);
          const formattedEmbed = formatMediaForUploader({ ...item, thumbnail_url: thumbnailUrl });

          if (formattedEmbed.id) {
            updateOrAddMedia(formattedEmbed);
            return formattedEmbed;
          }
          return [];
        });
      }
      return formattedNewMedia;
    } catch (error) {
      // silently fail
    }
  }, [updateOrAddMedia, spaceId, media.length]);

  const handleDelete = useCallback(async (mediaItem) => {
    const isImage = mediaItem.type !== 'video' && mediaItem.type !== '3d';
    const mediaId = mediaItem.id || mediaItem._id;

    const apiCall = isImage
      ? () => deleteSpaceImage(mediaId)
      : () => deleteSpaceEmbedMedia(spaceId, mediaId);

    try {
      await apiCall();
      const currentMedia = useUploaderStore.getState().media;
      const updatedAndReorderedMedia = enforceCoverPhotoRule(currentMedia, mediaId);
      setMedia(updatedAndReorderedMedia);
    } catch (error) {
      // silently fail
    }
  }, [spaceId, removeMedia]);

  const handleSave = useCallback(async () => {
    const imageUpdatePromises = [];
    const embedOrderPayload = [];

    media.forEach((item, index) => {
      const sortPayload = { id: item.id, sort_order: index + 1 };

      if (isEmbedMedia(item)) {
        embedOrderPayload.push(sortPayload);
      } else {
        imageUpdatePromises.push(updateSpaceImage({ id: item.id, payload: { sort_order: index + 1 } }));
      }
    });

    const allPromises = [...imageUpdatePromises];
    if (embedOrderPayload.length > 0) {
      allPromises.push(updateSpaceEmbedMediaOrder(spaceId, embedOrderPayload));
    }

    try {
      await Promise.all(allPromises);
    } catch {
      toast({
        title: '',
        description: <FormattedMessage id="uploader.toast.save_error" defaultMessage="Failed to save changes."/>,
        variant: 'destructive',
        duration: TOAST_DURATION
      });
    }
  }, [media, spaceId, toast]);

  return { fetchMedia, handlePhotoUpload, handleEmbedUpload, handleSave, handleDelete };
};
